import { useEffect, useState } from 'react';

export const useScrollPosition = (
  ref: React.RefObject<HTMLElement>,
  selector?: string,
) => {
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  // Capture ref.current during render to include in deps
  // This ensures effect re-runs when ref.current changes from null to an element
  const container = ref.current;

  useEffect(() => {
    if (!container) {
      return undefined;
    }

    // If selector provided, find child element; otherwise use container directly
    const element = selector
      ? container.querySelector<HTMLElement>(selector)
      : container;

    if (!element) {
      return undefined;
    }

    const handleScroll = () => {
      const { clientWidth, scrollLeft, scrollWidth } = element;

      // Add threshold of 5px to prevent flickering at boundaries
      const threshold = 5;
      const atStart = scrollLeft <= threshold;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - threshold;

      // Only update state if values actually changed
      setIsAtStart((prev) => (prev !== atStart ? atStart : prev));
      setIsAtEnd((prev) => (prev !== atEnd ? atEnd : prev));
    };

    // Check initial scroll position
    handleScroll();

    element.addEventListener('scroll', handleScroll);

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [container, selector]);

  return { isAtStart, isAtEnd };
};
